/**
 * Database Configuration section for the Test Suite Configuration page.
 *
 * Provides an expandable section with Redshift connection settings,
 * table configuration, performance tuning, secret ARN per environment,
 * and environment settings. Fields are pre-populated with sensible
 * defaults and can be overridden by the user.
 *
 * @see Requirements 17.1–17.5
 */

import React from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  FormField,
  Input,
  Select,
  Header,
  Container,
  ColumnLayout,
} from '@amzn/awsui-components-react';
import { ConfigPageAction, ConfigPageState, ValidationErrors } from './types';

// ─── Props ─────────────────────────────────────────────────────────────

export interface DatabaseConfigSectionProps {
  state: ConfigPageState;
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

// ─── Environment Options ───────────────────────────────────────────────

const ENVIRONMENT_OPTIONS = [
  { value: 'test', label: 'test' },
  { value: 'beta', label: 'beta' },
  { value: 'live', label: 'live' },
] as const;

const DEFAULT_ENVIRONMENT = 'test';

// ─── Component ─────────────────────────────────────────────────────────

const DatabaseConfigSection: React.FC<DatabaseConfigSectionProps> = ({
  state,
  dispatch,
  validationErrors,
}) => {
  const { databaseConfig } = state;

  const handleFieldChange = (path: string, value: string | number | boolean) => {
    dispatch({ type: 'UPDATE_FIELD', path, value });
  };

  const handleNumberChange = (path: string, rawValue: string) => {
    const parsed = parseInt(rawValue, 10);
    handleFieldChange(path, isNaN(parsed) ? rawValue : parsed);
  };

  return (
    <ExpandableSection
      headerText="Database"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="l">
        {/* ── Connection Settings ──────────────────────────────────── */}
        <Container header={<Header variant="h3">Connection Settings</Header>}>
          <ColumnLayout columns={2}>
            <FormField
              label="Type"
              description="Database engine type"
            >
              <Input
                value={databaseConfig.type}
                onChange={({ detail }) =>
                  handleFieldChange('databaseConfig.type', detail.value)
                }
                ariaLabel="Database type"
              />
            </FormField>

            <FormField
              label="Cluster identifier"
              description="Redshift cluster identifier"
            >
              <Input
                value={databaseConfig.cluster_identifier}
                onChange={({ detail }) =>
                  handleFieldChange('databaseConfig.cluster_identifier', detail.value)
                }
                ariaLabel="Cluster identifier"
              />
            </FormField>

            <FormField
              label="Database"
              description="Database name within the cluster"
            >
              <Input
                value={databaseConfig.database}
                onChange={({ detail }) =>
                  handleFieldChange('databaseConfig.database', detail.value)
                }
                ariaLabel="Database name"
              />
            </FormField>

            <FormField
              label="Stored procedure"
              description="Name of the stored procedure for metrics mapping"
            >
              <Input
                value={databaseConfig.stored_procedure}
                onChange={({ detail }) =>
                  handleFieldChange('databaseConfig.stored_procedure', detail.value)
                }
                ariaLabel="Stored procedure"
              />
            </FormField>

            <FormField
              label="Output schema"
              description="Schema for output tables"
            >
              <Input
                value={databaseConfig.output_schema}
                onChange={({ detail }) =>
                  handleFieldChange('databaseConfig.output_schema', detail.value)
                }
                ariaLabel="Output schema"
              />
            </FormField>
          </ColumnLayout>
        </Container>

        {/* ── Table Configuration ──────────────────────────────────── */}
        <Container header={<Header variant="h3">Table Configuration</Header>}>
          <SpaceBetween size="s">
            <ColumnLayout columns={2}>
              <FormField
                label="Source table name"
                description="Name of the source data table"
                errorText={validationErrors['databaseConfig.source_table_name']}
              >
                <Input
                  value={databaseConfig.source_table_name}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.source_table_name', detail.value)
                  }
                  placeholder="e.g. my_source_table"
                  ariaLabel="Source table name"
                />
              </FormField>

              <FormField
                label="Base metrics catalog"
                description="Table name for base metrics catalog"
                errorText={validationErrors['databaseConfig.base_metrics_catalog']}
              >
                <Input
                  value={databaseConfig.base_metrics_catalog}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.base_metrics_catalog', detail.value)
                  }
                  placeholder="e.g. base_metrics"
                  ariaLabel="Base metrics catalog"
                />
              </FormField>

              <FormField
                label="Derived metrics catalog"
                description="Table name for derived metrics catalog"
                errorText={validationErrors['databaseConfig.derived_metrics_catalog']}
              >
                <Input
                  value={databaseConfig.derived_metrics_catalog}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.derived_metrics_catalog', detail.value)
                  }
                  placeholder="e.g. derived_metrics"
                  ariaLabel="Derived metrics catalog"
                />
              </FormField>

              <FormField
                label="Default aggregation columns"
                description="JSON array of default aggregation column expressions"
              >
                <Input
                  value={databaseConfig.default_agg_columns}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.default_agg_columns', detail.value)
                  }
                  ariaLabel="Default aggregation columns"
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        {/* ── Performance ──────────────────────────────────────────── */}
        <Container header={<Header variant="h3">Performance</Header>}>
          <ColumnLayout columns={2}>
            <FormField
              label="Max workers"
              description="Maximum number of parallel database workers"
              errorText={validationErrors['databaseConfig.max_workers']}
            >
              <Input
                value={String(databaseConfig.max_workers)}
                onChange={({ detail }) =>
                  handleNumberChange('databaseConfig.max_workers', detail.value)
                }
                type="number"
                inputMode="numeric"
                ariaLabel="Max workers"
              />
            </FormField>
          </ColumnLayout>
        </Container>

        {/* ── Secret ARN by Environment ────────────────────────────── */}
        <Container header={<Header variant="h3">Secret ARN by Environment</Header>}>
          <SpaceBetween size="s">
            <ColumnLayout columns={2}>
              <FormField
                label="Test secret ARN"
                description="AWS Secrets Manager ARN for the test environment"
                errorText={validationErrors['databaseConfig.secret_arn.test']}
              >
                <Input
                  value={databaseConfig.secret_arn.test}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.secret_arn.test', detail.value)
                  }
                  placeholder="arn:aws:secretsmanager:..."
                  ariaLabel="Test secret ARN"
                />
              </FormField>

              <FormField
                label="Beta secret ARN"
                description="AWS Secrets Manager ARN for the beta environment"
                errorText={validationErrors['databaseConfig.secret_arn.beta']}
              >
                <Input
                  value={databaseConfig.secret_arn.beta}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.secret_arn.beta', detail.value)
                  }
                  placeholder="arn:aws:secretsmanager:..."
                  ariaLabel="Beta secret ARN"
                />
              </FormField>

              <FormField
                label="Live secret ARN"
                description="AWS Secrets Manager ARN for the live environment"
                errorText={validationErrors['databaseConfig.secret_arn.live']}
              >
                <Input
                  value={databaseConfig.secret_arn.live}
                  onChange={({ detail }) =>
                    handleFieldChange('databaseConfig.secret_arn.live', detail.value)
                  }
                  placeholder="arn:aws:secretsmanager:..."
                  ariaLabel="Live secret ARN"
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        {/* ── Environment Settings ─────────────────────────────────── */}
        <Container header={<Header variant="h3">Environment Settings</Header>}>
          <ColumnLayout columns={2}>
            <FormField
              label="Default environment"
              description="Environment to use when none is explicitly specified"
            >
              <Select
                selectedOption={{
                  value: databaseConfig.default_environment,
                  label: databaseConfig.default_environment,
                }}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'databaseConfig.default_environment',
                    detail.selectedOption.value ?? DEFAULT_ENVIRONMENT,
                  )
                }
                options={[...ENVIRONMENT_OPTIONS]}
                ariaLabel="Default environment"
              />
            </FormField>

            <FormField
              label="AWS profile"
              description="AWS CLI profile name (leave empty to use default credentials)"
            >
              <Input
                value={databaseConfig.aws_profile}
                onChange={({ detail }) =>
                  handleFieldChange('databaseConfig.aws_profile', detail.value)
                }
                placeholder="default"
                ariaLabel="AWS profile"
              />
            </FormField>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default DatabaseConfigSection;
